import { useEffect, useId, useState, type ReactNode } from 'react';
import { Alert, Button, DatePicker, FilterChip, IconCheckCircle, IconWarningCircle, Input } from '@indipia/ui-core';
import {
  DECLINE_REASONS,
  EMPTY_DRAFT,
  NOTE_MAX_LENGTH,
  TEXT,
  buildReplyPayload,
  submitGate,
  switchResult,
  validateReply,
  type OrderView,
  type ReplyDraft,
  type ReplyFieldKey,
  type ReplyPayload,
} from '../../pages/share/vendorOrderReply.logic';

// C-3-1 회신 폼 (OPEN). 선택 초기값 없음 — 필수 선택은 거래처가 직접 고른다.

function ChoiceGroup({ id, title, error, children }: { id: string; title: string; error?: string; children: ReactNode }) {
  return (
    <div role="group" aria-labelledby={id} className="flex flex-col gap-2">
      <div id={id} className="text-sm font-bold text-text-primary">
        {title} <span className="text-text-muted">{TEXT.required}</span>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
      {error && <p className="text-sm text-error" role="alert">{error}</p>}
    </div>
  );
}

export interface OrderReplyFormProps {
  view: OrderView;
  submitting: boolean;
  /** 폼 위 오류 문구 (서버·네트워크 오류) */
  formError: string | null;
  /** 서버 400 의 field 별 오류 문구 */
  serverFieldErrors: Partial<Record<ReplyFieldKey, string>>;
  onSubmit: (payload: ReplyPayload) => void;
}

export function OrderReplyForm({ view, submitting, formError, serverFieldErrors, onSubmit }: OrderReplyFormProps) {
  const uid = useId();
  const [draft, setDraft] = useState<ReplyDraft>(EMPTY_DRAFT);
  const [clientErrors, setClientErrors] = useState<{ availDate?: string; replyNote?: string }>({});

  const gate = submitGate(draft, view.liftReqYn);
  const dirty = draft.result !== null;

  // 입력한 선택이 있는 미제출 상태에서 이탈 보호 (제출 완료로 폼이 내려가면 해제)
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const dateError = clientErrors.availDate ?? serverFieldErrors.availDate;
  const noteError = clientErrors.replyNote ?? serverFieldErrors.replyNote;
  const resultError = serverFieldErrors.replyResultCode;
  const liftError = serverFieldErrors.liftAvailYn;
  const reasonError = serverFieldErrors.replyReasonCode;

  // 오류가 생기면 첫 오류 필드로 포커스
  useEffect(() => {
    const target = dateError ? `${uid}-date` : noteError ? `${uid}-note` : null;
    if (target) document.getElementById(target)?.focus();
  }, [dateError, noteError, uid]);

  const update = (patch: Partial<ReplyDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setClientErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !gate.ready) return;
    const errors = validateReply(draft);
    if (errors.availDate || errors.replyNote) {
      setClientErrors(errors);
      return;
    }
    onSubmit(buildReplyPayload(draft, view.liftReqYn));
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      {formError && <Alert kind="error" role="alert" aria-live="assertive">{formError}</Alert>}

      <ChoiceGroup id={`${uid}-result`} title={TEXT.availQuestion} error={resultError}>
        <FilterChip size="lg" active={draft.result === 'ACCEPTED'} aria-pressed={draft.result === 'ACCEPTED'}
          disabled={submitting} onClick={() => { setDraft((d) => switchResult(d, 'ACCEPTED')); setClientErrors({}); }}>
          <IconCheckCircle width={16} height={16} aria-hidden="true" /> {TEXT.accept}
        </FilterChip>
        <FilterChip size="lg" active={draft.result === 'DECLINED'} aria-pressed={draft.result === 'DECLINED'}
          disabled={submitting} onClick={() => { setDraft((d) => switchResult(d, 'DECLINED')); setClientErrors({}); }}>
          <IconWarningCircle width={16} height={16} aria-hidden="true" /> {TEXT.decline}
        </FilterChip>
      </ChoiceGroup>

      {draft.result === 'ACCEPTED' && view.liftReqYn === 'Y' && (
        <ChoiceGroup id={`${uid}-lift`} title={TEXT.liftAvailQuestion} error={liftError}>
          <FilterChip size="lg" active={draft.liftAvail === 'Y'} aria-pressed={draft.liftAvail === 'Y'}
            disabled={submitting} onClick={() => update({ liftAvail: 'Y' })}>
            {TEXT.accept}
          </FilterChip>
          <FilterChip size="lg" active={draft.liftAvail === 'N'} aria-pressed={draft.liftAvail === 'N'}
            disabled={submitting} onClick={() => update({ liftAvail: 'N' })}>
            {TEXT.decline}
          </FilterChip>
        </ChoiceGroup>
      )}

      {draft.result === 'DECLINED' && (
        <>
          <ChoiceGroup id={`${uid}-reason`} title={TEXT.reasonQuestion} error={reasonError}>
            {DECLINE_REASONS.map((r) => (
              <FilterChip key={r.code} size="lg" active={draft.reason === r.code} aria-pressed={draft.reason === r.code}
                disabled={submitting} onClick={() => update({ reason: r.code })}>
                {r.label}
              </FilterChip>
            ))}
          </ChoiceGroup>
          <DatePicker
            id={`${uid}-date`}
            size="lg"
            label={`${TEXT.availDateLabel} ${TEXT.optional}`}
            value={draft.availDate}
            onChange={(v) => update({ availDate: v })}
            error={dateError}
            disabled={submitting}
          />
          <div>
            <Input
              id={`${uid}-note`}
              size="lg"
              label={`${TEXT.noteLabel} ${TEXT.optional}`}
              value={draft.note}
              maxLength={NOTE_MAX_LENGTH}
              placeholder={TEXT.notePlaceholder}
              onChange={(e) => update({ note: e.target.value })}
              error={noteError}
              disabled={submitting}
            />
            <p className="mt-1 text-right text-xs text-text-muted">{draft.note.length}/{NOTE_MAX_LENGTH}</p>
          </div>
        </>
      )}

      <Alert kind="info">{TEXT.formNotice}</Alert>

      <Button type="submit" size="lg" className="w-full" disabled={!gate.ready} loading={submitting}>
        {gate.label}
      </Button>
    </form>
  );
}
